import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { categories } from '../data/projects';

const { FiUser, FiMail, FiGlobe, FiTag, FiUpload, FiX, FiSend, FiCheckCircle, FiGrid, FiAlertCircle } = FiIcons;

// ── Shared Image Upload ────────────────────────────────────────────────────────
const ImageUploader = ({ value, onChange }) => {
  const [error, setError] = useState('');

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      setError('Only PNG, JPEG, or WEBP images are allowed.');
      return;
    }
    // Limit file size to 2MB to prevent payload too large errors
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB.');
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onloadend = () => {
      onChange(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex gap-4 items-start w-full">
      <label className="flex-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#2a1f42] hover:border-purple-500/50 rounded-xl p-6 cursor-pointer transition-all bg-[#150a28] w-full">
        <SafeIcon icon={FiUpload} className="text-2xl text-white/30" />
        <span className="text-white/40 text-sm text-center">Click to upload cover image</span>
        <span className="text-white/20 text-xs">PNG, JPG, WEBP (Max 2MB)</span>
        <input type="file" accept="image/png, image/jpeg, image/jpg, image/webp" onChange={handleImage} className="hidden" />
      </label>
      {value && (
        <div className="relative w-24 h-24 sm:w-32 sm:h-24 rounded-xl overflow-hidden border border-[#2a1f42] flex-shrink-0">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange('')} className="absolute top-1 right-1 p-1 bg-black/70 rounded-full text-white/70 hover:text-white">
            <SafeIcon icon={FiX} className="text-xs" />
          </button>
        </div>
      )}
      {error && <p className="text-red-400 text-xs mt-1 absolute">{error}</p>}
    </div>
  );
};

const SubmitTool = () => {
  const [formData, setFormData] = useState({
    submitter_name: '',
    submitter_email: '',
    name: '',
    url: '',
    description: '',
    category: '',
    tags: '',
    image_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        status: 'pending', // Important: Force pending status
        dateAdded: new Date().toISOString(),
        featured: false
      };

      const { error: submitErr } = await supabase
        .from('saas_projects_1776800000001')
        .insert([payload]);

      if (submitErr) throw submitErr;

      // ---- Email Notification via Web3Forms ----
      // To activate: replace YOUR_WEB3FORMS_ACCESS_KEY with a key from web3forms.com
      // Or set VITE_WEB3FORMS_ACCESS_KEY in your .env file
      const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY || "YOUR_WEB3FORMS_ACCESS_KEY";
      
      if (accessKey && accessKey !== "YOUR_WEB3FORMS_ACCESS_KEY") {
        try {
          await fetch("https://api.web3forms.com/submit", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              access_key: accessKey,
              subject: `New Tool Submission: ${formData.name}`,
              from_name: "Fiverring Directory App",
              message: `
A new tool has been submitted and is pending your approval!

Submitter Details:
Name: ${formData.submitter_name}
Email: ${formData.submitter_email}

Tool Details:
Name: ${formData.name}
URL: ${formData.url}
Category: ${formData.category}

Description:
${formData.description}

Please log in to the Admin Dashboard to review and approve this submission.
              `,
            }),
          });
        } catch (emailErr) {
          console.error("Failed to send email notification", emailErr);
        }
      }
      
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Failed to submit tool. Please try again.');
      console.error('Submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 mb-6 shadow-lg shadow-purple-500/20">
          <SafeIcon icon={FiSend} className="text-3xl text-white" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Submit a Tool</h1>
        <p className="text-white/60 text-lg max-w-2xl mx-auto">
          Built an amazing AI tool? Share it with our community. All submissions are manually reviewed by our team before being published.
        </p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-[#0c0518] border border-[#392e4e] rounded-2xl p-6 md:p-10 shadow-2xl relative overflow-hidden">
        {success ? (
          <div className="text-center py-16">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-500" />
            </motion.div>
            <h3 className="text-2xl font-bold text-white mb-3">Submission Received!</h3>
            <p className="text-white/60 mb-8 max-w-md mx-auto">
              Thank you for submitting <strong>{formData.name}</strong>. Our team will review your tool and publish it if it meets our directory guidelines.
            </p>
            <Link to="/" className="inline-flex items-center justify-center px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all">
              Return to Directory
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
            {/* Submitter Info */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4 border-b border-[#2a1f42] pb-2">1. Your Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/60">Your Name *</label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                    <input type="text" name="submitter_name" required value={formData.submitter_name} onChange={handleChange} className="w-full bg-[#150a28] border border-[#392e4e] rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors" placeholder="Jane Doe" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/60">Email Address *</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                    <input type="email" name="submitter_email" required value={formData.submitter_email} onChange={handleChange} className="w-full bg-[#150a28] border border-[#392e4e] rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors" placeholder="jane@example.com" />
                  </div>
                </div>
              </div>
            </div>

            {/* Tool Info */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4 border-b border-[#2a1f42] pb-2">2. Tool Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/60">Tool Name *</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-[#150a28] border border-[#392e4e] rounded-xl py-3 px-4 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors" placeholder="e.g. Acme AI" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/60">Website URL *</label>
                  <div className="relative">
                    <SafeIcon icon={FiGlobe} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                    <input type="url" name="url" required value={formData.url} onChange={handleChange} className="w-full bg-[#150a28] border border-[#392e4e] rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors" placeholder="https://acme.ai" />
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <label className="text-sm font-medium text-white/60">Short Description *</label>
                <textarea name="description" required rows="3" value={formData.description} onChange={handleChange} className="w-full bg-[#150a28] border border-[#392e4e] rounded-xl py-3 px-4 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors resize-none" placeholder="Describe what your tool does in 1-2 sentences..." />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/60">Category *</label>
                  <div className="relative">
                    <SafeIcon icon={FiGrid} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                    <select name="category" required value={formData.category} onChange={handleChange} className="w-full bg-[#150a28] border border-[#392e4e] rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors appearance-none cursor-pointer">
                      <option value="" disabled>Select a category...</option>
                      {categories.filter(c => c.id !== 'all').map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/30">▼</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/60">Tags (Comma separated)</label>
                  <div className="relative">
                    <SafeIcon icon={FiTag} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                    <input type="text" name="tags" value={formData.tags} onChange={handleChange} className="w-full bg-[#150a28] border border-[#392e4e] rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors" placeholder="AI, Writing, Productivity" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-white/60">Cover Image</label>
                <ImageUploader value={formData.image_url} onChange={(val) => setFormData({...formData, image_url: val})} />
              </div>
            </div>

            {/* GDPR & Submit */}
            <div className="pt-4 border-t border-[#2a1f42]">
              <label className="flex items-start gap-4 p-5 bg-[#150a28] border border-purple-500/30 rounded-xl cursor-pointer hover:border-purple-500/60 transition-colors">
                <input type="checkbox" required className="mt-1 w-5 h-5 accent-purple-500 rounded bg-[#0c0518] border-[#392e4e] flex-shrink-0 cursor-pointer" />
                <span className="text-sm text-white/80 leading-relaxed">
                  I agree to the <Link to="/privacy-terms" className="text-purple-400 hover:underline font-bold">Terms of Service and Privacy Policy</Link>, and I explicitly consent to my submitted data being processed and stored in accordance with GDPR and international data protection regulations.
                </span>
              </label>

              {error && (
                <div className="mt-6 flex items-center gap-3 text-red-400 text-sm bg-red-500/10 p-4 rounded-xl border border-red-500/20">
                  <SafeIcon icon={FiAlertCircle} className="flex-shrink-0 text-lg" />
                  <p>{error}</p>
                </div>
              )}

              <button type="submit" disabled={loading} className="mt-6 w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-black text-lg py-4 px-6 rounded-xl shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2">
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <SafeIcon icon={FiSend} />
                    <span>Submit Tool for Review</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default SubmitTool;